/**
 * Frontend report calculation utilities.
 *
 * Merges:
 * - Availability data from the backend (capacity, holidays, leaves)
 * - Scheduled work items from monday.com (fetched via SDK)
 *
 * Produces report data entirely on the client side.
 * Supports day / week / month scopes via a unified column + aggregation system.
 */

import type {
  ReportConfig,
  ReportScope,
  ReportColumn,
  UtilizationReportResponse,
  UtilizationReportRow,
  UtilizationReportGroup,
  UtilizationCell,
  BoardSubRow,
  ItemSubRow,
  BoardResourcesReportResponse,
  BoardResourcesRow,
  BoardPersonSubRow,
} from '../api/report.api';
import type { MondayUser, MondayItem } from '../api/monday.api';
import type { BoardConfig } from '../api/board-config.api';
// ─── Types ──────────────────────────────────────────────────────────────────────

/** Daily availability from backend POST /reports/availability */
export interface DailyAvailability {
  date: string;
  capacityHours: number;
  holidayHours: number;
  leaveHours: number;
  availableHours: number;
  isHoliday: boolean;
  isLeave: boolean;
  isWeekend: boolean;
}

export interface UserAvailability {
  userId: string;
  totalAvailableHours: number;
  days: DailyAvailability[];
}

export interface AvailabilityResponse {
  startDate: string;
  endDate: string;
  users: UserAvailability[];
}

/** Scheduled work entry per user per day (parsed from monday.com items) */
export interface ScheduledWorkEntry {
  userId: string;
  boardId: string;
  boardName: string;
  itemId: string;
  itemName: string;
  date: string;
  hours: number;
  /** Original total hours for the item (before distribution). Used by reports for remaining estimate. */
  totalItemHours?: number;
  /** Work hour format of the source item */
  workHourFormat?: 'per_day' | 'total';
  /** Number of assignees the item's hours are split across */
  assigneeCount?: number;
}

// ─── Date utilities ─────────────────────────────────────────────────────────────

const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

const SHORT_MONTHS = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
];

function formatDate(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

function generateDateRange(startDate: string, endDate: string): string[] {
  const dates: string[] = [];
  const current = new Date(startDate + 'T00:00:00');
  const end = new Date(endDate + 'T00:00:00');
  while (current <= end) {
    dates.push(formatDate(current));
    current.setDate(current.getDate() + 1);
  }
  return dates;
}

export function addWeeks(date: Date, weeks: number): string {
  const result = new Date(date);
  result.setDate(result.getDate() + weeks * 7);
  return formatDate(result);
}

export function resolvePeriod(
  period: string,
  customStart?: string,
  customEnd?: string,
  weekStartsOn: 0 | 1 = 1,
): { startDate: string; endDate: string } {
  const today = new Date();
  const todayStr = formatDate(today);

  /** Align a date back to the configured first day of the week */
  const alignToWeekStart = (d: Date): Date => {
    const diff = (d.getDay() - weekStartsOn + 7) % 7;
    const result = new Date(d);
    result.setDate(d.getDate() - diff);
    return result;
  };

  switch (period) {
    case 'current_week': {
      const weekStart = alignToWeekStart(today);
      const weekEnd = new Date(weekStart);
      weekEnd.setDate(weekStart.getDate() + 6);
      return { startDate: formatDate(weekStart), endDate: formatDate(weekEnd) };
    }
    case 'previous_week': {
      const thisWeekStart = alignToWeekStart(today);
      const prevWeekStart = new Date(thisWeekStart);
      prevWeekStart.setDate(thisWeekStart.getDate() - 7);
      const prevWeekEnd = new Date(prevWeekStart);
      prevWeekEnd.setDate(prevWeekStart.getDate() + 6);
      return { startDate: formatDate(prevWeekStart), endDate: formatDate(prevWeekEnd) };
    }
    case 'previous_2_weeks': {
      const thisWeekStart = alignToWeekStart(today);
      const start = new Date(thisWeekStart);
      start.setDate(thisWeekStart.getDate() - 14);
      const end = new Date(thisWeekStart);
      end.setDate(thisWeekStart.getDate() - 1);
      return { startDate: formatDate(start), endDate: formatDate(end) };
    }
    case 'previous_4_weeks': {
      const thisWeekStart = alignToWeekStart(today);
      const start = new Date(thisWeekStart);
      start.setDate(thisWeekStart.getDate() - 28);
      const end = new Date(thisWeekStart);
      end.setDate(thisWeekStart.getDate() - 1);
      return { startDate: formatDate(start), endDate: formatDate(end) };
    }
    case 'next_2_weeks':
      return { startDate: todayStr, endDate: addWeeks(today, 2) };
    case 'next_4_weeks':
      return { startDate: todayStr, endDate: addWeeks(today, 4) };
    case 'current_and_next_4_weeks': {
      const weekStart = alignToWeekStart(today);
      const end = new Date(weekStart);
      end.setDate(weekStart.getDate() + 5 * 7 - 1); // current week + 4 more weeks
      return { startDate: formatDate(weekStart), endDate: formatDate(end) };
    }
    case 'next_8_weeks':
      return { startDate: todayStr, endDate: addWeeks(today, 8) };
    case 'next_12_weeks':
      return { startDate: todayStr, endDate: addWeeks(today, 12) };
    case 'current_month': {
      const start = new Date(today.getFullYear(), today.getMonth(), 1);
      const end = new Date(today.getFullYear(), today.getMonth() + 1, 0);
      return { startDate: formatDate(start), endDate: formatDate(end) };
    }
    case 'previous_month': {
      const start = new Date(today.getFullYear(), today.getMonth() - 1, 1);
      const end = new Date(today.getFullYear(), today.getMonth(), 0);
      return { startDate: formatDate(start), endDate: formatDate(end) };
    }
    case 'next_month': {
      const start = new Date(today.getFullYear(), today.getMonth() + 1, 1);
      const end = new Date(today.getFullYear(), today.getMonth() + 2, 0);
      return { startDate: formatDate(start), endDate: formatDate(end) };
    }
    case 'current_quarter': {
      const quarterStart = Math.floor(today.getMonth() / 3) * 3;
      const start = new Date(today.getFullYear(), quarterStart, 1);
      const end = new Date(today.getFullYear(), quarterStart + 3, 0);
      return { startDate: formatDate(start), endDate: formatDate(end) };
    }
    case 'next_quarter': {
      const currentQStart = Math.floor(today.getMonth() / 3) * 3;
      const nextQStart = currentQStart + 3;
      const year = nextQStart >= 12 ? today.getFullYear() + 1 : today.getFullYear();
      const month = nextQStart % 12;
      const start = new Date(year, month, 1);
      const end = new Date(year, month + 3, 0);
      return { startDate: formatDate(start), endDate: formatDate(end) };
    }
    case 'current_year': {
      const start = new Date(today.getFullYear(), 0, 1);
      const end = new Date(today.getFullYear(), 11, 31);
      return { startDate: formatDate(start), endDate: formatDate(end) };
    }
    case 'last_year': {
      const start = new Date(today.getFullYear() - 1, 0, 1);
      const end = new Date(today.getFullYear() - 1, 11, 31);
      return { startDate: formatDate(start), endDate: formatDate(end) };
    }
    case 'next_year': {
      const start = new Date(today.getFullYear() + 1, 0, 1);
      const end = new Date(today.getFullYear() + 1, 11, 31);
      return { startDate: formatDate(start), endDate: formatDate(end) };
    }
    case 'last_quarter': {
      const currentQStart = Math.floor(today.getMonth() / 3) * 3;
      const prevQStart = currentQStart - 3;
      const year = prevQStart < 0 ? today.getFullYear() - 1 : today.getFullYear();
      const month = ((prevQStart % 12) + 12) % 12;
      const start = new Date(year, month, 1);
      const end = new Date(year, month + 3, 0);
      return { startDate: formatDate(start), endDate: formatDate(end) };
    }
    case 'custom':
      return { startDate: customStart!, endDate: customEnd! };
    default:
      return { startDate: todayStr, endDate: addWeeks(today, 4) };
  }
}

// ═══════════════════════════════════════════════════════════════════════════════
// Unified Column Builders (Day / Week / Month)
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Build columns for the specified scope.
 */
export function buildColumns(
  startDate: string,
  endDate: string,
  scope: ReportScope,
  weekStartsOn: 0 | 1 = 1,
): ReportColumn[] {
  switch (scope) {
    case 'day':
      return buildDayColumns(startDate, endDate);
    case 'week':
      return buildWeekColumns(startDate, endDate, weekStartsOn);
    case 'month':
      return buildMonthColumns(startDate, endDate);
  }
}

function buildDayColumns(startDate: string, endDate: string): ReportColumn[] {
  const dates = generateDateRange(startDate, endDate);
  return dates.map((dateStr) => {
    const date = new Date(dateStr + 'T00:00:00');
    const dayOfWeek = date.getDay();
    return {
      key: dateStr,
      label: String(date.getDate()),
      startDate: dateStr,
      endDate: dateStr,
      isWeekend: dayOfWeek === 0 || dayOfWeek === 6,
    };
  });
}

function buildWeekColumns(startDate: string, endDate: string, weekStartsOn: 0 | 1 = 1): ReportColumn[] {
  const columns: ReportColumn[] = [];
  const start = new Date(startDate + 'T00:00:00');
  const end = new Date(endDate + 'T00:00:00');

  // Align to the configured first day of the week
  const dayOfWeek = start.getDay();
  const diff = (dayOfWeek - weekStartsOn + 7) % 7;
  const weekStart = new Date(start);
  weekStart.setDate(start.getDate() - diff);

  const current = new Date(weekStart);
  while (current <= end) {
    const weekEnd = new Date(current);
    weekEnd.setDate(current.getDate() + 6); // Sunday

    const startMonth = SHORT_MONTHS[current.getMonth()];
    const endMonth = SHORT_MONTHS[weekEnd.getMonth()];
    const label =
      startMonth === endMonth
        ? `${startMonth} ${current.getDate()} - ${weekEnd.getDate()}`
        : `${startMonth} ${current.getDate()} - ${endMonth} ${weekEnd.getDate()}`;

    columns.push({
      key: formatDate(current),
      label,
      startDate: formatDate(current),
      endDate: formatDate(weekEnd),
    });

    current.setDate(current.getDate() + 7);
  }

  return columns;
}

function buildMonthColumns(startDate: string, endDate: string): ReportColumn[] {
  const columns: ReportColumn[] = [];
  const end = new Date(endDate + 'T00:00:00');

  const start = new Date(startDate + 'T00:00:00');
  const current = new Date(start.getFullYear(), start.getMonth(), 1);

  while (current <= end) {
    const monthEnd = new Date(current.getFullYear(), current.getMonth() + 1, 0);
    const label = `${MONTH_NAMES[current.getMonth()]} ${current.getFullYear()}`;

    columns.push({
      key: formatDate(current),
      label,
      startDate: formatDate(current),
      endDate: formatDate(monthEnd),
    });

    current.setMonth(current.getMonth() + 1);
  }

  return columns;
}

// ═══════════════════════════════════════════════════════════════════════════════
// Aggregation Utilities
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Aggregate daily utilization cells into the specified columns.
 * For 'day' scope columns map 1:1, so this is effectively a pass-through.
 */
function aggregateCells(
  dailyCells: UtilizationCell[],
  columns: ReportColumn[],
  scope: ReportScope,
): UtilizationCell[] {
  if (scope === 'day') return dailyCells;

  // Build a map for fast lookup
  const cellByDate = new Map<string, UtilizationCell>();
  for (const cell of dailyCells) {
    cellByDate.set(cell.startDate, cell);
  }

  return columns.map((col) => {
    let scheduledHours = 0;
    let availableHours = 0;

    // Sum all daily cells within this column's date range
    const dates = generateDateRange(col.startDate, col.endDate);
    for (const date of dates) {
      const cell = cellByDate.get(date);
      if (cell) {
        scheduledHours += cell.scheduledHours;
        availableHours += cell.availableHours;
      }
    }

    scheduledHours = Math.round(scheduledHours * 100) / 100;
    availableHours = Math.round(availableHours * 100) / 100;

    const utilizationRate =
      availableHours > 0
        ? Math.round((scheduledHours / availableHours) * 10000) / 100
        : 0;

    return {
      startDate: col.startDate,
      endDate: col.endDate,
      availableHours,
      scheduledHours,
      utilizationRate,
      isHoliday: false,
      isLeave: false,
      isWeekend: false,
    };
  });
}

/**
 * Aggregate daily hours into column buckets for board resources report.
 * dailyHours[i] corresponds to allDates[i].
 */
function aggregateHoursCells(
  dailyHours: number[],
  allDates: string[],
  columns: ReportColumn[],
  scope: ReportScope,
): number[] {
  if (scope === 'day') {
    return dailyHours.map((h) => Math.round(h * 100) / 100);
  }

  // Build column index: for each date, which column does it belong to?
  const dateToColIdx = new Map<string, number>();
  for (let ci = 0; ci < columns.length; ci++) {
    const col = columns[ci];
    const dates = generateDateRange(col.startDate, col.endDate);
    for (const d of dates) {
      dateToColIdx.set(d, ci);
    }
  }

  const result = new Array(columns.length).fill(0);
  for (let i = 0; i < allDates.length; i++) {
    const ci = dateToColIdx.get(allDates[i]);
    if (ci !== undefined) {
      result[ci] += dailyHours[i];
    }
  }

  return result.map((h) => Math.round(h * 100) / 100);
}

// ─── Item parsing (monday.com column values) ────────────────────────────────────

function parseTimeline(
  value: string | null,
): { from: string; to: string } | null {
  if (!value) return null;
  try {
    const parsed = JSON.parse(value) as { from?: string; to?: string };
    if (parsed.from && parsed.to) return { from: parsed.from, to: parsed.to };
  } catch { /* ignore */ }
  return null;
}

// ─── Board items data ─────────────────────────────────────────────────────────

export interface BoardItemsData {
  boardId: string;
  boardName: string;
  items: MondayItem[];
  /** Subitems fetched separately from the subitems board (flat array, not nested under parents). */
  subItems?: MondayItem[];
  config: BoardConfig;
}

// ═══════════════════════════════════════════════════════════════════════════════
// Utilization Forecast Report
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Build the full utilization forecast report from availability + scheduled work.
 */
export function buildUtilizationReport(
  availability: AvailabilityResponse,
  scheduledWork: ScheduledWorkEntry[],
  config: ReportConfig,
  users: MondayUser[],
  weekStartsOn: 0 | 1 = 1,
): UtilizationReportResponse {
  const { startDate, endDate } = availability;
  const scope: ReportScope = config.scope ?? 'day';
  const columns = buildColumns(startDate, endDate, scope, weekStartsOn);

  // Build user metadata lookup
  const userMeta = new Map<
    string,
    { name: string; photo: string | null; teamName: string | null; teamIds: string[]; teamNames: Map<string, string> }
  >();
  for (const u of users) {
    const uid = String(u.id);
    const firstTeam = u.teams.length > 0 ? u.teams[0] : null;
    const teamNames = new Map<string, string>();
    for (const t of u.teams) {
      teamNames.set(String(t.id), t.name);
    }
    userMeta.set(uid, {
      name: u.name,
      photo: u.photo_thumb_small,
      teamName: firstTeam?.name ?? null,
      teamIds: u.teams.map((t) => String(t.id)),
      teamNames,
    });
  }

  // Always build user rows — sub-rows depend on groupBy
  const includeBoardBreakdown = config.groupBy === 'board';
  const includeItemBreakdown = config.groupBy === 'item';
  const rows = buildUserRows(
    availability,
    scheduledWork,
    userMeta,
    includeBoardBreakdown,
    includeItemBreakdown,
    scope,
    columns,
  );

  // Group rows (team grouping or flat)
  const selectedTeamIds = config.teamIds && config.teamIds.length > 0
    ? new Set(config.teamIds)
    : null;
  const groups = groupRows(rows, config.groupBy, userMeta, selectedTeamIds);

  // Compute summary totals
  const totalAvailableHours = rows.reduce(
    (sum, r) => sum + r.totalAvailableHours,
    0,
  );
  const totalScheduledHours = rows.reduce(
    (sum, r) => sum + r.totalScheduledHours,
    0,
  );

  return {
    reportType: 'utilization_forecast',
    generatedAt: new Date().toISOString(),
    period: { startDate, endDate },
    scope,
    columns,
    groups,
    summary: {
      totalAvailableHours: Math.round(totalAvailableHours * 100) / 100,
      totalScheduledHours: Math.round(totalScheduledHours * 100) / 100,
      overallUtilizationRate:
        totalAvailableHours > 0
          ? Math.round(
              (totalScheduledHours / totalAvailableHours) * 10000,
            ) / 100
          : 0,
      totalUsers: rows.length,
    },
  };
}

// ─── User rows ───────────────────────────────────────────────────────────────

function buildUserRows(
  availability: AvailabilityResponse,
  scheduledWork: ScheduledWorkEntry[],
  userMeta: Map<string, { name: string; photo: string | null; teamName: string | null }>,
  includeBoardBreakdown: boolean,
  includeItemBreakdown: boolean,
  scope: ReportScope,
  columns: ReportColumn[],
): UtilizationReportRow[] {
  // Index scheduled work by userId+date (total across all boards)
  const scheduledByUserDate = new Map<string, number>();
  for (const work of scheduledWork) {
    const key = `${work.userId}:${work.date}`;
    scheduledByUserDate.set(key, (scheduledByUserDate.get(key) ?? 0) + work.hours);
  }

  // For board breakdown: index by userId+boardId+date
  const scheduledByUserBoardDate = includeBoardBreakdown
    ? new Map<string, number>()
    : null;
  const userBoardNames = includeBoardBreakdown
    ? new Map<string, Map<string, string>>() // userId → (boardId → boardName)
    : null;

  if (includeBoardBreakdown) {
    for (const work of scheduledWork) {
      const key = `${work.userId}:${work.boardId}:${work.date}`;
      scheduledByUserBoardDate!.set(key, (scheduledByUserBoardDate!.get(key) ?? 0) + work.hours);
      if (!userBoardNames!.has(work.userId)) userBoardNames!.set(work.userId, new Map());
      userBoardNames!.get(work.userId)!.set(work.boardId, work.boardName);
    }
  }

  // For item breakdown: index by userId+itemId+date
  const scheduledByUserItemDate = includeItemBreakdown
    ? new Map<string, number>()
    : null;
  const userItemInfo = includeItemBreakdown
    ? new Map<string, Map<string, { itemName: string; boardId: string; boardName: string }>>()
    : null;

  if (includeItemBreakdown) {
    for (const work of scheduledWork) {
      const key = `${work.userId}:${work.itemId}:${work.date}`;
      scheduledByUserItemDate!.set(key, (scheduledByUserItemDate!.get(key) ?? 0) + work.hours);
      if (!userItemInfo!.has(work.userId)) userItemInfo!.set(work.userId, new Map());
      userItemInfo!.get(work.userId)!.set(work.itemId, {
        itemName: work.itemName,
        boardId: work.boardId,
        boardName: work.boardName,
      });
    }
  }

  const rows: UtilizationReportRow[] = [];

  for (const userAvail of availability.users) {
    const meta = userMeta.get(userAvail.userId);
    if (!meta) continue;

    let totalScheduledHours = 0;

    // Always build DAILY cells first
    const dailyCells: UtilizationCell[] = userAvail.days.map((day) => {
      const key = `${userAvail.userId}:${day.date}`;
      const scheduledHours = Math.round(
        (scheduledByUserDate.get(key) ?? 0) * 100,
      ) / 100;
      totalScheduledHours += scheduledHours;

      const utilizationRate =
        day.availableHours > 0
          ? Math.round((scheduledHours / day.availableHours) * 10000) / 100
          : 0;

      return {
        startDate: day.date,
        endDate: day.date,
        availableHours: day.availableHours,
        scheduledHours,
        utilizationRate,
        isHoliday: day.isHoliday,
        isLeave: day.isLeave,
        isWeekend: day.isWeekend,
      };
    });

    // Aggregate to scope
    const cells = aggregateCells(dailyCells, columns, scope);

    totalScheduledHours = Math.round(totalScheduledHours * 100) / 100;
    const utilizationRate =
      userAvail.totalAvailableHours > 0
        ? Math.round(
            (totalScheduledHours / userAvail.totalAvailableHours) * 10000,
          ) / 100
        : 0;

    // Build board sub-rows if needed
    let boardSubRows: BoardSubRow[] | undefined;
    if (includeBoardBreakdown && userBoardNames!.has(userAvail.userId)) {
      const boardMap = userBoardNames!.get(userAvail.userId)!;
      boardSubRows = [];

      for (const [boardId, boardName] of boardMap.entries()) {
        let boardTotalScheduled = 0;

        const boardDailyCells: UtilizationCell[] = userAvail.days.map((day) => {
          const key = `${userAvail.userId}:${boardId}:${day.date}`;
          const scheduledHours = Math.round(
            (scheduledByUserBoardDate!.get(key) ?? 0) * 100,
          ) / 100;
          boardTotalScheduled += scheduledHours;

          const boardUtilRate =
            day.availableHours > 0
              ? Math.round((scheduledHours / day.availableHours) * 10000) / 100
              : 0;

          return {
            startDate: day.date,
            endDate: day.date,
            availableHours: day.availableHours,
            scheduledHours,
            utilizationRate: boardUtilRate,
            isHoliday: day.isHoliday,
            isLeave: day.isLeave,
            isWeekend: day.isWeekend,
          };
        });

        const boardCells = aggregateCells(boardDailyCells, columns, scope);

        boardTotalScheduled = Math.round(boardTotalScheduled * 100) / 100;
        const boardUtilRate =
          userAvail.totalAvailableHours > 0
            ? Math.round(
                (boardTotalScheduled / userAvail.totalAvailableHours) * 10000,
              ) / 100
            : 0;

        boardSubRows.push({
          boardId,
          boardName,
          totalScheduledHours: boardTotalScheduled,
          utilizationRate: boardUtilRate,
          cells: boardCells,
        });
      }
    }

    // Build item sub-rows if needed
    let itemSubRows: ItemSubRow[] | undefined;
    if (includeItemBreakdown && userItemInfo!.has(userAvail.userId)) {
      const itemMap = userItemInfo!.get(userAvail.userId)!;
      itemSubRows = [];

      for (const [itemId, info] of itemMap.entries()) {
        let itemTotalScheduled = 0;

        const itemDailyCells: UtilizationCell[] = userAvail.days.map((day) => {
          const key = `${userAvail.userId}:${itemId}:${day.date}`;
          const scheduledHours = Math.round(
            (scheduledByUserItemDate!.get(key) ?? 0) * 100,
          ) / 100;
          itemTotalScheduled += scheduledHours;

          const itemUtilRate =
            day.availableHours > 0
              ? Math.round((scheduledHours / day.availableHours) * 10000) / 100
              : 0;

          return {
            startDate: day.date,
            endDate: day.date,
            availableHours: day.availableHours,
            scheduledHours,
            utilizationRate: itemUtilRate,
            isHoliday: day.isHoliday,
            isLeave: day.isLeave,
            isWeekend: day.isWeekend,
          };
        });

        const itemCells = aggregateCells(itemDailyCells, columns, scope);

        itemTotalScheduled = Math.round(itemTotalScheduled * 100) / 100;
        const itemUtilRate =
          userAvail.totalAvailableHours > 0
            ? Math.round(
                (itemTotalScheduled / userAvail.totalAvailableHours) * 10000,
              ) / 100
            : 0;

        itemSubRows.push({
          itemId,
          itemName: info.itemName,
          boardId: info.boardId,
          boardName: info.boardName,
          totalScheduledHours: itemTotalScheduled,
          utilizationRate: itemUtilRate,
          cells: itemCells,
        });
      }
    }

    rows.push({
      userId: userAvail.userId,
      userName: meta.name,
      userPhoto: meta.photo,
      teamName: meta.teamName,
      totalAvailableHours: userAvail.totalAvailableHours,
      totalScheduledHours,
      utilizationRate,
      cells,
      boardSubRows,
      itemSubRows,
    });
  }

  return rows;
}

// ─── Grouping helpers ────────────────────────────────────────────────────────

function groupRows(
  rows: UtilizationReportRow[],
  groupBy: string,
  userMeta: Map<string, { teamName: string | null; teamIds: string[]; teamNames: Map<string, string> }>,
  selectedTeamIds: Set<string> | null,
): UtilizationReportGroup[] {
  if (groupBy === 'team') {
    return groupByTeam(rows, userMeta, selectedTeamIds);
  }
  // 'none', 'board', 'item' — single group (board/item are sub-rows, not group-level)
  const totals = computeGroupTotals(rows);
  return [{ groupId: 'all', groupName: 'All Users', ...totals, rows }];
}

function groupByTeam(
  rows: UtilizationReportRow[],
  userMeta: Map<string, { teamName: string | null; teamIds: string[]; teamNames: Map<string, string> }>,
  selectedTeamIds: Set<string> | null,
): UtilizationReportGroup[] {
  const groupMap = new Map<string, UtilizationReportRow[]>();

  for (const row of rows) {
    const meta = userMeta.get(row.userId);
    const teamIds = meta?.teamIds ?? [];

    const relevantTeamIds = selectedTeamIds
      ? teamIds.filter((tid) => selectedTeamIds.has(tid))
      : teamIds;

    if (relevantTeamIds.length === 0) {
      const key = 'unassigned:Unassigned';
      if (!groupMap.has(key)) groupMap.set(key, []);
      groupMap.get(key)!.push(row);
    } else {
      for (const teamId of relevantTeamIds) {
        const teamName = meta?.teamNames.get(teamId) ?? 'Unassigned';
        const key = `${teamId}:${teamName}`;
        if (!groupMap.has(key)) groupMap.set(key, []);
        groupMap.get(key)!.push(row);
      }
    }
  }

  return Array.from(groupMap.entries()).map(([key, groupRows]) => {
    const [groupId, ...nameParts] = key.split(':');
    const groupName = nameParts.join(':');
    const totals = computeGroupTotals(groupRows);
    return { groupId, groupName, ...totals, rows: groupRows };
  });
}

function computeGroupTotals(rows: UtilizationReportRow[]): {
  totalAvailableHours: number;
  totalScheduledHours: number;
  utilizationRate: number;
} {
  const totalAvailableHours = rows.reduce(
    (sum, r) => sum + r.totalAvailableHours,
    0,
  );
  const totalScheduledHours = rows.reduce(
    (sum, r) => sum + r.totalScheduledHours,
    0,
  );
  return {
    totalAvailableHours: Math.round(totalAvailableHours * 100) / 100,
    totalScheduledHours: Math.round(totalScheduledHours * 100) / 100,
    utilizationRate:
      totalAvailableHours > 0
        ? Math.round(
            (totalScheduledHours / totalAvailableHours) * 10000,
          ) / 100
        : 0,
  };
}

// ═══════════════════════════════════════════════════════════════════════════════
// Board Resources Forecast
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Build the Board Resources Forecast report from scheduled work data.
 *
 * Rows are boards, with person sub-rows underneath each board.
 * Columns are based on the specified scope (day / week / month).
 */
export function buildBoardResourcesReport(
  scheduledWork: ScheduledWorkEntry[],
  users: MondayUser[],
  startDate: string,
  endDate: string,
  scope: ReportScope = 'week',
  weekStartsOn: 0 | 1 = 1,
): BoardResourcesReportResponse {
  const columns = buildColumns(startDate, endDate, scope, weekStartsOn);
  const allDates = generateDateRange(startDate, endDate);

  // Build user name lookup
  const userNames = new Map<string, string>();
  for (const u of users) {
    userNames.set(String(u.id), u.name);
  }

  // Index: boardId → { boardName, users: Map<userId, dailyHours[]> }
  const boardData = new Map<
    string,
    {
      boardName: string;
      users: Map<string, number[]>; // daily hours array indexed by allDates index
    }
  >();

  for (const work of scheduledWork) {
    const dayIndex = allDates.indexOf(work.date);
    if (dayIndex < 0) continue;

    if (!boardData.has(work.boardId)) {
      boardData.set(work.boardId, {
        boardName: work.boardName,
        users: new Map(),
      });
    }
    const board = boardData.get(work.boardId)!;

    if (!board.users.has(work.userId)) {
      board.users.set(work.userId, new Array(allDates.length).fill(0));
    }
    board.users.get(work.userId)![dayIndex] += work.hours;
  }

  // Build rows with aggregation
  const rows: BoardResourcesRow[] = [];
  let grandTotalScheduled = 0;

  for (const [boardId, board] of boardData) {
    const personSubRows: BoardPersonSubRow[] = [];
    const boardDailyHours = new Array(allDates.length).fill(0);

    for (const [userId, dailyHours] of board.users) {
      // Aggregate daily → scope
      const cells = aggregateHoursCells(dailyHours, allDates, columns, scope);
      const totalHours = Math.round(
        cells.reduce((sum, h) => sum + h, 0) * 100,
      ) / 100;

      personSubRows.push({
        userId,
        userName: userNames.get(userId) ?? userId,
        totalScheduledHours: totalHours,
        cells,
      });

      // Accumulate into board totals
      for (let i = 0; i < dailyHours.length; i++) {
        boardDailyHours[i] += dailyHours[i];
      }
    }

    // Sort person sub-rows by total hours descending
    personSubRows.sort((a, b) => b.totalScheduledHours - a.totalScheduledHours);

    // Aggregate board row
    const boardCells = aggregateHoursCells(boardDailyHours, allDates, columns, scope);
    const boardTotal = Math.round(
      boardCells.reduce((sum, h) => sum + h, 0) * 100,
    ) / 100;

    grandTotalScheduled += boardTotal;

    rows.push({
      boardId,
      boardName: board.boardName,
      totalScheduledHours: boardTotal,
      cells: boardCells,
      personSubRows,
    });
  }

  // Sort board rows by total hours descending
  rows.sort((a, b) => b.totalScheduledHours - a.totalScheduledHours);

  return {
    reportType: 'board_resources_forecast',
    generatedAt: new Date().toISOString(),
    period: { startDate, endDate },
    scope,
    columns,
    rows,
    summary: {
      totalScheduledHours: Math.round(grandTotalScheduled * 100) / 100,
      totalBoards: rows.length,
    },
  };
}
